<script setup>
import {
  ref,
  watch,
  nextTick,
  onBeforeUnmount,
  useTemplateRef,
} from 'vue';
import { createPopper } from '@popperjs/core';

const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  readonly: { type: Boolean, default: false },
});

const emits = defineEmits(['update:modelValue']);

// Estado reactivo
const valor = ref(props.modelValue);
const pickerVisible = ref(false);
const popperInstance = ref(null);
const emojiPickerElement = useTemplateRef('emojiPickerElement');
const emojiButton = useTemplateRef('emojiButton');

// Watchers
watch(() => props.modelValue, (nuevoValor) => {
  valor.value = nuevoValor;
});

watch(valor, (nuevoValor) => {
  emits('update:modelValue', nuevoValor);
});

// Métodos
const destruirPopper = () => {
  if (popperInstance.value) {
    popperInstance.value.destroy();
    popperInstance.value = null;
  }
};

// Manejar clic fuera del picker
const handleClickOutside = (event) => {
  if (pickerVisible.value && emojiPickerElement.value) {
    if (!emojiPickerElement.value.contains(event.target)
        && !emojiButton.value.contains(event.target)) {
      pickerVisible.value = false;
      destruirPopper();
      document.removeEventListener('click', handleClickOutside);
    }
  }
};

const cerrarPicker = () => {
  pickerVisible.value = false;
  destruirPopper();
  document.removeEventListener('click', handleClickOutside);
};

const abrirPicker = () => {
  if (props.disabled || props.readonly) return;

  pickerVisible.value = true;

  nextTick(() => {
    if (emojiButton.value && emojiPickerElement.value) {
      popperInstance.value = createPopper(emojiButton.value, emojiPickerElement.value, {
        placement: 'bottom-start',
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 8],
            },
          },
          {
            name: 'preventOverflow',
            options: {
              boundary: 'viewport',
              padding: 16,
            },
          },
          {
            name: 'flip',
            options: {
              fallbackPlacements: ['top-start', 'bottom-end', 'top-end'],
            },
          },
        ],
      });

      // Agregar listener para clic fuera
      document.addEventListener('click', handleClickOutside);
    }
  });
};

const alternarPicker = () => {
  if (pickerVisible.value) {
    cerrarPicker();
  } else {
    abrirPicker();
  }
};

const alSeleccionarEmoji = (evento) => {
  // emoji-picker-element emite evento.detail.unicode
  valor.value += evento.detail.unicode;
  cerrarPicker();
};

// Lifecycle
onBeforeUnmount(() => {
  cerrarPicker();
});
</script>

<template>
  <div class="input-emoji-picker">
    <div class="input-group">
      <input
        v-model="valor"
        type="text"
        class="form-control"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
      />
      <span class="input-group-btn">
        <button
          ref="emojiButton"
          type="button"
          class="btn btn-default"
          :disabled="disabled"
          @click="alternarPicker"
        >
          <i class="fa fa-smile"></i>
        </button>
      </span>
    </div>

    <!-- Emoji Picker Popup usando Popper.js -->
    <div
      v-if="pickerVisible"
      ref="emojiPickerElement"
      class="emoji-picker-popup"
      @click.stop
    >
      <emoji-picker
        class="emoji-picker light"
        @emoji-click="alSeleccionarEmoji"
      ></emoji-picker>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.input-emoji-picker {
  position: relative;
}

/* Estilos para el popup de emoji con Popper.js */
.emoji-picker-popup {
  z-index: 9999;
  background: white;
  border: 1px solid #ccc;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  max-width: 350px;
  max-height: 400px;
}

.emoji-picker-popup .emoji-picker {
  border: none;
  box-shadow: none;
  --emoji-size: 2.5rem;
}
</style>
